import { observer } from 'mobx-react';
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  makeStyles,
} from '@material-ui/core';

import ArchiveStore from '../../../store/ArchiveStore';


const useStyles = makeStyles(() => {
  return {
    container: {
      position: 'relative',
      width: '100%',
      marginTop: 16,
    },
    listContainer: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 10,
      maxHeight: 300,
      overflowY: 'auto',
      borderRadius: 4,
      boxShadow: '0px 2px 10px rgba(0,0,0,0.15)',
    },
    option: {
      fontSize: 16,
      cursor: 'pointer',
      padding: '8px 12px',
      '&:hover': { backgroundColor: '#f0f0f0' },
    },
  };
});

function SegmentFilters({ collapsing, segments, clearFilter }) {
  const classes = useStyles();
  const inputRef = useRef(null);
  const history = useHistory();

  const {
    searchFilterString,
    selectedFilter,
    handleSearchInputChange,
  } = ArchiveStore;

  const [highlightIndex, setHighlightIndex] = useState(-1);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const filteredSegments = useMemo(() => {
    if (!Array.isArray(segments)) {
      return [];
    }
    const unique = segments.filter(
      (s, i, arr) => arr.findIndex((x) => x.id === s.id) === i
    );
    const sorted = unique.sort((a, b) => a.name.localeCompare(b.name));
    if (!searchFilterString?.trim()) {
      return sorted;
    }

    return sorted.filter((s) =>
      s.name.toLowerCase().includes(searchFilterString.toLowerCase())
    );
  }, [segments, searchFilterString]);

  const getSegmentLink = useCallback((segment) => {
    const slug = segment.name.toLowerCase().replace(/\s+/g, '-');

    return segment.type === 'vintage' ? `/${slug}-v${segment.id}` : `/archives?segmentid=${segment.id}`;
  }, []);

  const handleOptionClick = useCallback(
    (segment) => {
      clearFilter();
      history.push(getSegmentLink(segment));
    },
    [clearFilter, history, getSegmentLink]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!filteredSegments.length) {
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => (prev + 1) % filteredSegments.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev <= 0 ? filteredSegments.length - 1 : prev - 1
        );
      } else if (e.key === 'Enter' && highlightIndex >= 0) {
        e.preventDefault();
        handleOptionClick(filteredSegments[highlightIndex]);
      }
    },
    [filteredSegments, highlightIndex, handleOptionClick]
  );

  const handleMouseEnter = useCallback((index) => {
    setHighlightIndex(index);
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      handleSearchInputChange(e);
    },
    [handleSearchInputChange]
  );

  const isAllOptionsSearchFilter = selectedFilter === '';

  if (!collapsing || filteredSegments.length === 0) {
    return null;
  }

  return (
    <Box className={classes.container}>
      <TextField
        autoFocus={true}
        fullWidth={true}
        inputRef={inputRef}
        label="Search Segments"
        placeholder="Start typing to filter segments..."
        size="small"
        style={{ minWidth: 320 }}
        value={searchFilterString}
        variant="outlined"
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />

      <Paper className={classes.listContainer}>
        <List disablePadding={true}>
          {filteredSegments.map((segment, index) => (
            <ListItem
              key={segment.id}
              className={classes.option}
              style={{
                backgroundColor:
                  index === highlightIndex ? '#e8e8e8' : 'transparent',
              }}
              onClick={() => handleOptionClick(segment)}
              onMouseEnter={() => handleMouseEnter(index)}
            >
              {segment.name}
              {isAllOptionsSearchFilter &&
                segment.type === 'vintage' &&
                ' (vintage)'}
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
}

export default observer(SegmentFilters);
